/**
 * Stone Investment Projects List View
 * 
 * List view for projects with Stone Investment's luxury aesthetic:
 * - Editorial list layout
 * - Horizontal cards
 * - Filtering and sorting
 */

'use client';

import { FormatPrice } from '@/components/ui/FormatPrice';
import { DEFAULT_CURRENCY } from '@/lib/config/app.config';
import Link from 'next/link';
import Image from 'next/image';
import { useState } from 'react';
import { ArrowUpRight, MapPin, Building, Calendar } from 'lucide-react';
import { detectRegion } from '../utils/regionColors';

export interface ProjectListItem {
  id: string;
  name: string;
  slug: string;
  tagline?: string;
  description?: string;
  location?: string;
  image?: string;
  status?: 'PLANNING' | 'UNDER_CONSTRUCTION' | 'COMPLETED' | 'SELLING';
  totalUnits?: number;
  completionDate?: string;
  priceFrom?: number;
  currency?: string;
}

export interface StoneProjectsListViewProps {
  /**
   * Projects to display
   */
  projects: ProjectListItem[];
  /**
   * Base path for project links
   */
  basePath?: string;
  /**
   * Current locale
   */
  locale?: string;
  /**
   * Empty state message
   */
  emptyMessage?: string;
  /**
   * Translation messages
   */
  messages?: Record<string, string>;
  /**
   * Custom className
   */
  className?: string;
}

/**
 * Get status display text
 */
function getStatusLabel(status?: string, messages?: Record<string, string>): string {
  if (messages) {
    const map: Record<string, string> = {
      PLANNING: messages.stageInPlanning || '',
      UNDER_CONSTRUCTION: messages.stageUnderConstruction || '',
      COMPLETED: messages.stageCompleted || '',
      SELLING: messages.stageNowSelling || '',
    };
    if (status && map[status]) return map[status];
  }
  switch (status) {
    case 'PLANNING': return 'In Planning';
    case 'UNDER_CONSTRUCTION': return 'Under Construction';
    case 'COMPLETED': return 'Completed';
    case 'SELLING': return 'Now Selling';
    default: return '';
  }
}

/**
 * Individual project list item
 */
function ProjectListCard({ project, basePath, locale, messages }: {
  project: ProjectListItem;
  basePath: string;
  locale: string;
  messages?: Record<string, string>;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const region = detectRegion(project.location || project.name);
  const isFrance = region === 'provence';
  const href = `/${locale}${basePath}/${project.slug}`;
  
  return (
    <Link
      href={href}
      className="group block"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <article className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 py-8 border-b border-stone-200 hover:bg-stone-50/50 transition-colors -mx-6 px-6">
        {/* Image */}
        <div className="lg:col-span-4 xl:col-span-3">
          <div className="relative aspect-[4/3] lg:aspect-[3/2] overflow-hidden bg-stone-200">
            {project.image ? (
              <Image
                src={project.image}
                alt={project.name}
                fill
                className={`object-cover transition-transform duration-[2000ms] ease-out ${
                  isHovered ? 'scale-105' : 'scale-100'
                }`}
                sizes="(max-width: 1024px) 100vw, 33vw"
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-stone-300 to-stone-200" />
            )}
            
            {/* Status Badge */}
            {project.status && (
              <div className="absolute top-4 left-4 px-3 py-1.5 bg-stone-900 text-[10px] uppercase tracking-[0.2em] text-stone-100 font-sans">
                {getStatusLabel(project.status, messages)}
              </div>
            )}
            
            {/* Flag Colour Strip */}
            <div className="absolute bottom-0 left-0 right-0 h-1 flex">
              {isFrance ? (
                <>
                  <div className={`h-full flex-1 bg-[#002654] transition-opacity duration-500 ${isHovered ? 'opacity-100' : 'opacity-0'}`} />
                  <div className={`h-full flex-1 bg-white transition-opacity duration-500 ${isHovered ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '100ms' }} />
                  <div className={`h-full flex-1 bg-[#ED2939] transition-opacity duration-500 ${isHovered ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '200ms' }} />
                </>
              ) : (
                <>
                  <div className={`h-full flex-1 bg-[#EA2839] transition-opacity duration-500 ${isHovered ? 'opacity-100' : 'opacity-0'}`} />
                  <div className={`h-full flex-1 bg-[#1A206D] transition-opacity duration-500 ${isHovered ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '75ms' }} />
                  <div className={`h-full flex-1 bg-[#FFD500] transition-opacity duration-500 ${isHovered ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '150ms' }} />
                  <div className={`h-full flex-1 bg-[#00A551] transition-opacity duration-500 ${isHovered ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '225ms' }} />
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="lg:col-span-6 xl:col-span-7 flex flex-col justify-center">
          {/* Location */}
          {project.location && (
            <div className="flex items-center gap-2 text-stone-500 mb-2">
              <MapPin size={12} strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-[0.2em] font-sans">{project.location}</span>
            </div>
          )}
          
          {/* Title */}
          <h2 className={`font-serif text-2xl lg:text-3xl text-stone-900 leading-tight mb-3 transition-all duration-500 ${
            isHovered ? 'italic' : ''
          }`}>
            {project.name}
          </h2>
          
          {/* Tagline or Description */}
          <p className="font-sans text-stone-600 text-sm lg:text-base line-clamp-2 mb-4">
            {project.tagline || project.description}
          </p>
          
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 lg:gap-6 text-sm font-sans text-stone-600">
            {project.totalUnits && (
              <span className="flex items-center gap-1.5">
                <Building size={14} strokeWidth={1.5} className="text-stone-400" />
                {project.totalUnits} {messages?.units || 'Units'}
              </span>
            )}
            {project.completionDate && (
              <span className="flex items-center gap-1.5">
                <Calendar size={14} strokeWidth={1.5} className="text-stone-400" />
                {project.completionDate}
              </span>
            )}
          </div>
        </div>
        
        {/* Price & Action */}
        <div className="lg:col-span-2 flex flex-col justify-center items-start lg:items-end">
          {project.priceFrom && (
            <div className="mb-4">
              <p className="text-xs text-stone-500 font-sans uppercase tracking-wide mb-1">{messages?.from || 'From'}</p>
              <FormatPrice
                amount={project.priceFrom}
                currency={project.currency || DEFAULT_CURRENCY}
                size="lg"
                className="font-serif text-stone-900"
              />
            </div>
          )}
          
          <div className={`transition-all duration-500 ${isHovered ? 'opacity-100 translate-x-0' : 'opacity-50 -translate-x-2 lg:translate-x-2'}`}>
            <ArrowUpRight size={24} className="text-stone-900" strokeWidth={1} />
          </div>
        </div>
      </article>
    </Link>
  );
}

/**
 * StoneProjectsListView - List view for projects
 */
export function StoneProjectsListView({
  projects,
  basePath = '/projects',
  locale = 'en',
  emptyMessage = 'No projects found',
  messages = {},
  className = '',
}: StoneProjectsListViewProps) {
  if (projects.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-4 text-center">
        <div className="w-20 h-20 border border-stone-300 flex items-center justify-center mb-8">
          <Building size={32} className="text-stone-400" strokeWidth={1} />
        </div>
        <h3 className="font-serif text-3xl text-stone-900 mb-3 italic">{emptyMessage}</h3>
        <p className="text-stone-500 font-sans text-sm tracking-wide">
          {messages.emptyDescription || 'Check back soon for new developments.'}
        </p>
      </div>
    );
  }
  
  return (
    <div className={`divide-y divide-stone-200 ${className}`}>
      {projects.map((project) => (
        <ProjectListCard
          key={project.id}
          project={project}
          basePath={basePath}
          locale={locale}
          messages={messages}
        />
      ))}
    </div>
  );
}

export default StoneProjectsListView;
